import { useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";
import { API } from "@/App";
import { useAuth } from "@/contexts/AuthContext";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import {
  Plus, Trash, X, CheckCircle, Spinner, ArrowsClockwise, Coins, House, Crown,
  Mountains, CurrencyBtc, GraduationCap, Car, AirplaneTilt, Briefcase, ShieldStar,
  Egg, Lightbulb, Trophy, ChartLineUp, Calendar, CaretRight, CaretLeft, Lightning,
  Database, Pencil
} from "@phosphor-icons/react";
import {
  ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip, CartesianGrid
} from "recharts";

const CATEGORY_META = {
  emergency: { label: "Emergency Fund", icon: ShieldStar, color: "emerald", suggest: { name: "Build $10k Emergency Nest", target: 10000, months: 12 } },
  house: { label: "House Down Payment", icon: House, color: "blue", suggest: { name: "$60k Down Payment", target: 60000, months: 60 } },
  retirement: { label: "Retirement Nest", icon: Crown, color: "amber", suggest: { name: "Retire with $1M", target: 1000000, months: 360 } },
  debt: { label: "Debt Freedom", icon: Mountains, color: "red", suggest: { name: "Crush $25k of Debt", target: 25000, months: 36 } },
  crypto: { label: "Crypto Portfolio", icon: CurrencyBtc, color: "violet", suggest: { name: "Hit $100k Crypto", target: 100000, months: 60 } },
  education: { label: "Education", icon: GraduationCap, color: "blue", suggest: { name: "Tuition Fund", target: 30000, months: 48 } },
  vehicle: { label: "Vehicle", icon: Car, color: "zinc", suggest: { name: "New Car Fund", target: 20000, months: 36 } },
  vacation: { label: "Travel", icon: AirplaneTilt, color: "blue", suggest: { name: "Dream Trip Fund", target: 8000, months: 18 } },
  business: { label: "Start a Business", icon: Briefcase, color: "amber", suggest: { name: "Launch Capital", target: 50000, months: 36 } },
  other: { label: "Other", icon: Coins, color: "amber", suggest: { name: "My Goal", target: 10000, months: 24 } },
};

const RISK_META = {
  conservative: { label: "Conservative", apr: 0.04, blurb: "HYSA + bonds. Low risk, ~4% returns." },
  balanced: { label: "Balanced", apr: 0.06, blurb: "Index funds + some bonds. ~6% returns." },
  aggressive: { label: "Aggressive", apr: 0.08, blurb: "Stocks + crypto. ~8% long-run, bumpy ride." },
  custom: { label: "Custom", apr: 0.07, blurb: "Set your own expected return rate." },
};

const INFLATION_RATE = 0.03;

/** Resolve the effective APR for a goal, honoring custom_apr when risk = "custom". */
function aprFor(risk, customApr) {
  if (risk === "custom" && customApr != null && customApr > 0) return Number(customApr);
  return RISK_META[risk]?.apr ?? 0.06;
}

/**
 * Numeric % input that doesn't fight the user. Maintains an internal
 * string buffer so partial entries like "" or "7." stay typeable. Calls
 * onChange(decimal) live whenever the buffer parses to a valid number,
 * and re-formats nicely on blur.
 */
function PercentInput({ value, onChange, dataTestId, className = "" }) {
  // value is a decimal (0.07 = 7%)
  const fmt = (v) => (v == null || isNaN(v) ? "" : (Number(v) * 100).toString());
  const [str, setStr] = useState(() => fmt(value));

  // Re-sync if outer value changes via something else (e.g. user clicked a different risk pill)
  useEffect(() => {
    setStr(prev => {
      const parsed = Number(prev);
      const prevDecimal = isNaN(parsed) ? null : parsed / 100;
      // Only overwrite if parent value diverges meaningfully from our buffer
      if (prevDecimal == null || Math.abs(prevDecimal - (value ?? 0)) > 1e-6) return fmt(value);
      return prev;
    });
  }, [value]);

  const commit = () => {
    const n = Number(str);
    const clamped = isNaN(n) ? 0 : Math.min(50, Math.max(0, n));
    onChange(clamped / 100);
    setStr(clamped.toString());
  };

  return (
    <input
      type="number"
      min="0"
      max="50"
      step="0.1"
      inputMode="decimal"
      value={str}
      onChange={e => {
        const v = e.target.value;
        setStr(v);
        if (v === "" || v === "." || v === "-") return; // wait until input is parseable
        const n = Number(v);
        if (!isNaN(n)) onChange(Math.min(0.5, Math.max(0, n / 100)));
      }}
      onBlur={commit}
      data-testid={dataTestId}
      className={className}
    />
  );
}

// ------------------------- math helpers -------------------------
function monthsBetween(targetDate) {
  const t = new Date(targetDate);
  const now = new Date();
  const months = (t.getFullYear() - now.getFullYear()) * 12 + (t.getMonth() - now.getMonth());
  return Math.max(months, 0);
}

function fmtMoney(v) {
  if (v === null || v === undefined || isNaN(v)) return "—";
  const n = Number(v);
  if (Math.abs(n) >= 1_000_000) return `$${(n / 1_000_000).toFixed(2)}M`;
  if (Math.abs(n) >= 10_000) return `$${(n / 1000).toFixed(1)}k`;
  return `$${n.toLocaleString(undefined, { maximumFractionDigits: 2 })}`;
}

/** Required monthly contribution to reach `target` from `present`, with given annual rate over n months. */
function requiredMonthly(present, target, annualRate, months) {
  if (months <= 0) return Math.max(target - present, 0);
  const r = annualRate / 12;
  const fvPresent = present * Math.pow(1 + r, months);
  const need = target - fvPresent;
  if (need <= 0) return 0;
  if (r === 0) return need / months;
  return need / (((Math.pow(1 + r, months) - 1) / r));
}

/** Project monthly balance with compound growth + monthly contributions for a given duration. */
function projectGrowth(present, monthlyContribution, annualRate, months) {
  const r = annualRate / 12;
  const points = [];
  let bal = present;
  for (let i = 0; i <= months; i++) {
    points.push({ month: i, value: Math.round(bal * 100) / 100 });
    bal = bal * (1 + r) + monthlyContribution;
  }
  return points;
}

function inflationAdjust(target, years) {
  return target * Math.pow(1 + INFLATION_RATE, years);
}

// ------------------------- Nest Gauge -------------------------
function NestGauge({ pct, size = 140 }) {
  const safe = Math.max(0, Math.min(100, pct || 0));
  const stroke = 10;
  const radius = (size - stroke) / 2;
  const circ = 2 * Math.PI * radius;
  const offset = circ - (safe / 100) * circ;
  return (
    <div className="relative inline-flex items-center justify-center" style={{ width: size, height: size }}>
      <svg width={size} height={size} className="transform -rotate-90">
        <defs>
          <linearGradient id="goldGrad" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#fde68a" />
            <stop offset="50%" stopColor="#f59e0b" />
            <stop offset="100%" stopColor="#b45309" />
          </linearGradient>
        </defs>
        <circle cx={size / 2} cy={size / 2} r={radius} stroke="rgba(63,63,70,0.6)" strokeWidth={stroke} fill="none" />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          stroke="url(#goldGrad)"
          strokeWidth={stroke}
          strokeLinecap="round"
          fill="none"
          strokeDasharray={circ}
          strokeDashoffset={offset}
          style={{ transition: "stroke-dashoffset 600ms ease-out" }}
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <Egg size={size * 0.22} weight="fill" className="text-amber-400 mb-0.5 drop-shadow-[0_0_6px_rgba(251,191,36,0.5)]" />
        <span className="font-data text-base font-bold text-zinc-50">{safe.toFixed(0)}%</span>
      </div>
    </div>
  );
}

// ------------------------- Goal card -------------------------
function GoalCard({ goal, onLog, onEdit, onDelete }) {
  const meta = CATEGORY_META[goal.category] || CATEGORY_META.other;
  const Icon = meta.icon;
  const pct = (goal.current_amount / goal.target_amount) * 100;
  const months = monthsBetween(goal.target_date);
  const apr = aprFor(goal.risk, goal.custom_apr);
  const required = requiredMonthly(goal.current_amount, goal.target_amount, apr, months);
  const onTrack = goal.monthly_contribution >= required - 0.5;
  const completed = goal.current_amount >= goal.target_amount;

  return (
    <div
      className={`rounded-sm border p-4 transition-colors ${
        completed
          ? "border-amber-500/60 bg-gradient-to-br from-amber-500/15 via-zinc-900/50 to-zinc-950"
          : "border-zinc-800 bg-zinc-900/40 hover:border-amber-500/30"
      }`}
      data-testid={`goal-card-${goal.id}`}
    >
      <div className="flex items-start gap-4">
        <NestGauge pct={pct} size={110} />
        <div className="min-w-0 flex-1">
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0">
              <div className="flex items-center gap-1.5 flex-wrap">
                <Icon size={13} weight="bold" className={`text-${meta.color}-400 shrink-0`} />
                <h3 className="font-heading text-sm font-bold text-zinc-100 leading-tight truncate">{goal.name}</h3>
                {completed && (
                  <Badge className="text-[8px] h-4 px-1.5 py-0 border-0 bg-amber-500/25 text-amber-200">
                    <Trophy size={9} weight="fill" className="inline mr-0.5" /> COMPLETE
                  </Badge>
                )}
              </div>
              <p className="text-[10px] text-zinc-500 mt-0.5">{meta.label}</p>
            </div>
            <div className="flex items-center gap-1 shrink-0">
              <button onClick={() => onEdit(goal)} className="p-1 text-zinc-500 hover:text-amber-300" title="Edit"><Pencil size={11} /></button>
              <button onClick={() => onDelete(goal)} className="p-1 text-zinc-500 hover:text-red-400" title="Delete"><Trash size={11} /></button>
            </div>
          </div>

          <div className="mt-2 grid grid-cols-2 gap-2 text-[11px]">
            <div>
              <p className="text-[9px] uppercase text-zinc-600">Current</p>
              <p className="font-data text-zinc-100">{fmtMoney(goal.current_amount)}</p>
            </div>
            <div>
              <p className="text-[9px] uppercase text-zinc-600">Target</p>
              <p className="font-data text-amber-300">{fmtMoney(goal.target_amount)}</p>
            </div>
            <div>
              <p className="text-[9px] uppercase text-zinc-600">Time left</p>
              <p className="font-data text-zinc-300">{months} mo</p>
            </div>
            <div>
              <p className="text-[9px] uppercase text-zinc-600">Need / month</p>
              <p className={`font-data ${onTrack ? "text-emerald-300" : "text-amber-300"}`}>
                {fmtMoney(required)}
              </p>
            </div>
          </div>

          <div className="mt-2.5 flex items-center justify-between gap-2">
            <p className="text-[10px] text-zinc-500">
              {onTrack
                ? `On track at $${goal.monthly_contribution.toFixed(0)}/mo`
                : `Need ${fmtMoney(required - goal.monthly_contribution)} more / mo`}
            </p>
            <button
              onClick={() => onLog(goal)}
              data-testid={`goal-log-${goal.id}`}
              className="inline-flex items-center gap-1 text-[10px] px-2 py-1 rounded-sm border border-emerald-500/30 bg-emerald-500/10 text-emerald-300 hover:bg-emerald-500/20"
            >
              <Plus size={10} weight="bold" /> Log
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ------------------------- Wizard -------------------------
function GoalWizard({ initial, nestEgg, onCancel, onSave, saving }) {
  const isEdit = !!initial?.id;
  const [step, setStep] = useState(1);
  const today = new Date().toISOString().slice(0, 10);
  const defaultDate = new Date(Date.now() + 365 * 24 * 3600 * 1000).toISOString().slice(0, 10);
  const [form, setForm] = useState({
    name: initial?.name || "",
    category: initial?.category || "emergency",
    description: initial?.description || "",
    starting_amount: initial?.starting_amount ?? 0,
    target_amount: initial?.target_amount ?? "",
    target_date: initial?.target_date || defaultDate,
    monthly_contribution: initial?.monthly_contribution ?? 0,
    risk: initial?.risk || "balanced",
    custom_apr: initial?.custom_apr ?? 0.07,
  });
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const applySuggestion = (cat) => {
    const sug = CATEGORY_META[cat]?.suggest;
    if (!sug) return;
    const date = new Date();
    date.setMonth(date.getMonth() + sug.months);
    set("category", cat);
    set("name", sug.name);
    set("target_amount", sug.target);
    set("target_date", date.toISOString().slice(0, 10));
  };

  const useNestEgg = () => {
    if (!nestEgg?.available) return;
    set("starting_amount", Math.max(0, Math.round(nestEgg.net_worth)));
    if (!form.monthly_contribution && nestEgg.monthly_savings > 0) {
      set("monthly_contribution", Math.round(nestEgg.monthly_savings));
    }
  };

  const canNext1 = form.category && form.name.trim().length > 0;
  const canNext2 = Number(form.target_amount) > Number(form.starting_amount) && form.target_date >= today;

  const handleSave = () => {
    onSave({
      ...form,
      starting_amount: Number(form.starting_amount) || 0,
      target_amount: Number(form.target_amount),
      monthly_contribution: Number(form.monthly_contribution) || 0,
      custom_apr: form.risk === "custom" ? Number(form.custom_apr) : null,
    });
  };

  return (
    <div className="rounded-sm border border-amber-500/40 bg-zinc-900/70 p-5 space-y-4" data-testid="goal-wizard">
      <div className="flex items-center justify-between">
        <p className="text-xs font-medium text-amber-300">{isEdit ? "Edit Goal" : `New Goal — Step ${step} of 3`}</p>
        <button onClick={onCancel} className="text-zinc-500 hover:text-zinc-300"><X size={14} /></button>
      </div>

      {/* Step 1: Category + Name */}
      {(step === 1 || isEdit) && (
        <div className="space-y-3" data-testid="wizard-step-1">
          {!isEdit && <p className="text-[11px] text-zinc-500">Pick a category. We'll suggest a starting goal you can edit.</p>}
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-1.5">
            {Object.entries(CATEGORY_META).map(([key, m]) => {
              const Icon = m.icon;
              const active = form.category === key;
              return (
                <button
                  key={key}
                  type="button"
                  onClick={() => (isEdit ? set("category", key) : applySuggestion(key))}
                  data-testid={`goal-cat-${key}`}
                  className={`flex flex-col items-center gap-1 px-2 py-3 rounded-sm border text-[10px] transition-colors ${
                    active
                      ? "border-amber-500/60 bg-amber-500/15 text-amber-200"
                      : "border-zinc-800 bg-zinc-900/40 text-zinc-400 hover:border-zinc-700"
                  }`}
                >
                  <Icon size={16} weight="bold" className={active ? "text-amber-300" : `text-${m.color}-400`} />
                  {m.label}
                </button>
              );
            })}
          </div>
          <div>
            <label className="text-[10px] text-zinc-500">Goal name</label>
            <input
              value={form.name}
              onChange={e => set("name", e.target.value.slice(0, 80))}
              placeholder="e.g. Build $10k Emergency Nest"
              data-testid="wizard-name"
              className="w-full mt-1 bg-zinc-950/60 border border-zinc-800 rounded-sm px-3 py-2 text-xs text-zinc-100 focus:outline-none focus:border-amber-500/50"
            />
          </div>
          <div>
            <label className="text-[10px] text-zinc-500">Description (optional)</label>
            <input
              value={form.description}
              onChange={e => set("description", e.target.value.slice(0, 300))}
              placeholder="Why this goal matters to you"
              className="w-full mt-1 bg-zinc-950/60 border border-zinc-800 rounded-sm px-3 py-2 text-xs text-zinc-300 focus:outline-none focus:border-amber-500/50"
            />
          </div>
        </div>
      )}

      {/* Step 2: Amounts */}
      {(step === 2 || isEdit) && (
        <div className="space-y-3" data-testid="wizard-step-2">
          {nestEgg?.available && !isEdit && (
            <button
              type="button"
              onClick={useNestEgg}
              data-testid="wizard-use-nest-egg"
              className="w-full inline-flex items-center justify-center gap-2 px-3 py-2 rounded-sm border border-amber-500/40 bg-amber-500/10 text-amber-300 hover:bg-amber-500/20 text-[11px] font-medium"
            >
              <Database size={12} weight="bold" />
              Use my Nest Egg data — last snapshot: {fmtMoney(nestEgg.net_worth)} net worth
            </button>
          )}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-[10px] text-zinc-500">Starting amount</label>
              <div className="relative mt-1">
                <span className="absolute left-2 top-1/2 -translate-y-1/2 text-zinc-600 text-xs">$</span>
                <input
                  type="number" min="0" step="any"
                  value={form.starting_amount}
                  onChange={e => set("starting_amount", e.target.value)}
                  data-testid="wizard-starting"
                  className="w-full bg-zinc-950/60 border border-zinc-800 rounded-sm pl-5 pr-2 py-2 text-xs text-zinc-100 font-data focus:outline-none focus:border-amber-500/50"
                />
              </div>
            </div>
            <div>
              <label className="text-[10px] text-zinc-500">Target amount</label>
              <div className="relative mt-1">
                <span className="absolute left-2 top-1/2 -translate-y-1/2 text-zinc-600 text-xs">$</span>
                <input
                  type="number" min="1" step="any"
                  value={form.target_amount}
                  onChange={e => set("target_amount", e.target.value)}
                  data-testid="wizard-target"
                  className="w-full bg-zinc-950/60 border border-zinc-800 rounded-sm pl-5 pr-2 py-2 text-xs text-amber-300 font-data focus:outline-none focus:border-amber-500/50"
                />
              </div>
            </div>
          </div>
          <div>
            <label className="text-[10px] text-zinc-500">Target date</label>
            <input
              type="date"
              min={today}
              value={form.target_date}
              onChange={e => set("target_date", e.target.value)}
              data-testid="wizard-date"
              className="w-full mt-1 bg-zinc-950/60 border border-zinc-800 rounded-sm px-3 py-2 text-xs text-zinc-100 focus:outline-none focus:border-amber-500/50"
            />
          </div>
        </div>
      )}

      {/* Step 3: Risk + monthly */}
      {(step === 3 || isEdit) && (
        <div className="space-y-3" data-testid="wizard-step-3">
          <p className="text-[10px] text-zinc-500 uppercase tracking-wider">Risk level — sets expected return rate</p>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-1.5">
            {Object.entries(RISK_META).map(([k, r]) => {
              const active = form.risk === k;
              return (
                <button
                  key={k}
                  type="button"
                  onClick={() => set("risk", k)}
                  data-testid={`wizard-risk-${k}`}
                  className={`p-2.5 rounded-sm border text-left transition-colors ${
                    active ? "border-amber-500/60 bg-amber-500/15" : "border-zinc-800 bg-zinc-900/40 hover:border-zinc-700"
                  }`}
                >
                  <p className={`text-[11px] font-bold ${active ? "text-amber-300" : "text-zinc-200"}`}>{r.label}</p>
                  <p className="text-[9px] text-zinc-500 mt-0.5">
                    {k === "custom" ? `${(form.custom_apr * 100).toFixed(form.custom_apr * 100 % 1 === 0 ? 0 : 1)}%/yr` : `${(r.apr * 100).toFixed(0)}%/yr`}
                  </p>
                  <p className="text-[9px] text-zinc-600 mt-1 leading-snug">{r.blurb}</p>
                </button>
              );
            })}
          </div>
          {form.risk === "custom" && (
            <div data-testid="wizard-custom-apr-row">
              <label className="text-[10px] text-zinc-500">Expected annual return (%)</label>
              <div className="relative mt-1 max-w-xs">
                <PercentInput
                  value={form.custom_apr}
                  onChange={v => set("custom_apr", v)}
                  dataTestId="wizard-custom-apr"
                  className="w-full bg-zinc-950/60 border border-amber-500/40 rounded-sm pr-8 pl-3 py-2 text-xs text-amber-200 font-data focus:outline-none focus:border-amber-500"
                />
                <span className="absolute right-2 top-1/2 -translate-y-1/2 text-zinc-600 text-xs">%</span>
              </div>
              <p className="text-[9px] text-zinc-600 mt-1">Tip: 4–5% = HYSA / bonds · 6–8% = stock index · 10–15% = aggressive equities · &gt;20% = aspirational.</p>
            </div>
          )}
          <div>
            <label className="text-[10px] text-zinc-500">Monthly contribution (optional)</label>
            <div className="relative mt-1">
              <span className="absolute left-2 top-1/2 -translate-y-1/2 text-zinc-600 text-xs">$</span>
              <input
                type="number" min="0" step="any"
                value={form.monthly_contribution}
                onChange={e => set("monthly_contribution", e.target.value)}
                data-testid="wizard-monthly"
                className="w-full bg-zinc-950/60 border border-zinc-800 rounded-sm pl-5 pr-2 py-2 text-xs text-zinc-100 font-data focus:outline-none focus:border-amber-500/50"
              />
            </div>
            <p className="text-[10px] text-zinc-600 mt-1">We'll calculate the required monthly amount automatically based on your target.</p>
          </div>
        </div>
      )}

      <div className="flex items-center justify-between pt-2">
        {!isEdit && step > 1 && (
          <button onClick={() => setStep(s => s - 1)} className="text-[11px] text-zinc-400 hover:text-zinc-200 inline-flex items-center gap-1"><CaretLeft size={11} /> Back</button>
        )}
        <div className="ml-auto flex items-center gap-2">
          <button onClick={onCancel} className="text-[11px] px-3 py-1.5 rounded-sm bg-zinc-800/60 border border-zinc-700 text-zinc-300 hover:bg-zinc-800">Cancel</button>
          {!isEdit && step < 3 && (
            <button
              onClick={() => setStep(s => s + 1)}
              disabled={(step === 1 && !canNext1) || (step === 2 && !canNext2)}
              data-testid="wizard-next"
              className="text-[11px] px-3 py-1.5 rounded-sm bg-amber-500/20 border border-amber-500/50 text-amber-200 hover:bg-amber-500/30 disabled:opacity-40 inline-flex items-center gap-1"
            >
              Next <CaretRight size={11} />
            </button>
          )}
          {(isEdit || step === 3) && (
            <button
              onClick={handleSave}
              disabled={saving || !canNext1 || !canNext2}
              data-testid="wizard-save"
              className="text-[11px] px-3 py-1.5 rounded-sm bg-amber-500/25 border border-amber-500/55 text-amber-100 hover:bg-amber-500/35 disabled:opacity-40 inline-flex items-center gap-1"
            >
              {saving ? <Spinner size={11} className="animate-spin" /> : <CheckCircle size={11} />}
              {isEdit ? "Save changes" : "Create goal"}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// ------------------------- Detail / What-if panel -------------------------
function GoalDetail({ goal, onLog, onEdit, onDelete, onClose }) {
  const meta = CATEGORY_META[goal.category] || CATEGORY_META.other;
  const months = monthsBetween(goal.target_date);
  const yearsLeft = months / 12;

  // Sliders
  const [contrib, setContrib] = useState(goal.monthly_contribution || 0);
  const [riskKey, setRiskKey] = useState(goal.risk || "balanced");
  const [customApr, setCustomApr] = useState(goal.custom_apr || 0.07);
  const [adjustForInflation, setAdjustForInflation] = useState(false);

  const apr = aprFor(riskKey, customApr);
  const required = requiredMonthly(goal.current_amount, goal.target_amount, apr, months);
  const projection = useMemo(
    () => projectGrowth(goal.current_amount, contrib, apr, months),
    [goal.current_amount, contrib, apr, months]
  );
  const projectedFinal = projection[projection.length - 1]?.value ?? goal.current_amount;
  const inflTarget = inflationAdjust(goal.target_amount, yearsLeft);
  const dispTarget = adjustForInflation ? inflTarget : goal.target_amount;
  const willHit = projectedFinal >= dispTarget;

  // Nugget tip selection (rule-based)
  const nugget = (() => {
    if (months <= 18) return { title: "Short-term goal — keep it boring", text: "Money you need within 18 months has no business in stocks. Park it in a HYSA at 4–5% APY (FDIC-insured) so a market dip can't push your goal further away.", link: "/financial-education?tab=emergency-fund" };
    if (goal.category === "debt") return { title: "Avalanche beats vibes", text: "Paying the highest-APR debt first saves the most money mathematically. Snowball (smallest balance first) wins on psychology — pick the one you'll actually finish.", link: "/financial-education?tab=debt-destroyer" };
    if (goal.category === "retirement" || months >= 120) return { title: "Compounding does the heavy lifting", text: "On a 30-year timeline, the difference between 6% and 8% returns isn't 33% more — it's roughly DOUBLE the final value. Start now, invest consistently, ignore the noise.", link: "/financial-education?tab=compound-interest" };
    if (goal.category === "crypto") return { title: "Position size like a pro", text: "Even max-aggressive crypto allocations rarely exceed 10–20% of net worth. The goal is to survive every cycle long enough to benefit from the next one.", link: "/academy" };
    return { title: "Automate before you motivate", text: "Set the monthly contribution to auto-transfer on payday, before you can feel it. Willpower runs out — auto-pay doesn't.", link: "/financial-education?tab=budgeting" };
  })();

  return (
    <div className="rounded-sm border border-amber-500/30 bg-zinc-900/40 p-5 space-y-4" data-testid="goal-detail">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex items-start gap-3">
          <NestGauge pct={(goal.current_amount / goal.target_amount) * 100} size={120} />
          <div className="min-w-0">
            <p className="text-[10px] uppercase tracking-wider text-amber-400 font-bold">{meta.label}</p>
            <h2 className="font-heading text-xl font-bold text-zinc-50 leading-tight">{goal.name}</h2>
            {goal.description && <p className="text-[11px] text-zinc-400 mt-1 max-w-md">{goal.description}</p>}
            <div className="flex items-center gap-3 mt-2 text-[11px] flex-wrap">
              <span className="text-zinc-500">Current <span className="text-zinc-100 font-data">{fmtMoney(goal.current_amount)}</span></span>
              <span className="text-zinc-500">Target <span className="text-amber-300 font-data">{fmtMoney(dispTarget)}</span>{adjustForInflation && <span className="text-[9px] text-zinc-600 ml-1">(infl. adj.)</span>}</span>
              <span className="text-zinc-500"><Calendar size={10} className="inline mr-1" />{months} months</span>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-1.5 shrink-0">
          <button onClick={() => onLog(goal)} data-testid="goal-detail-log" className="inline-flex items-center gap-1 text-[10px] px-2 py-1 rounded-sm border border-emerald-500/30 bg-emerald-500/10 text-emerald-300 hover:bg-emerald-500/20"><Plus size={10} weight="bold" /> Log</button>
          <button onClick={() => onEdit(goal)} className="p-1.5 text-zinc-500 hover:text-amber-300 border border-zinc-800 rounded-sm" title="Edit"><Pencil size={11} /></button>
          <button onClick={() => onDelete(goal)} className="p-1.5 text-zinc-500 hover:text-red-400 border border-zinc-800 rounded-sm" title="Delete"><Trash size={11} /></button>
          <button onClick={onClose} className="p-1.5 text-zinc-500 hover:text-zinc-300 border border-zinc-800 rounded-sm" title="Close"><X size={11} /></button>
        </div>
      </div>

      {/* What-if controls */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
        <div className="lg:col-span-2 rounded-sm border border-zinc-800 bg-zinc-950/40 p-3">
          <p className="text-[10px] uppercase tracking-wider text-zinc-500 mb-2">Projection — what-if simulator</p>
          <div className="h-48">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={projection} margin={{ top: 5, right: 8, left: 0, bottom: 0 }}>
                <defs>
                  <linearGradient id="goalArea" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#fbbf24" stopOpacity={0.4} />
                    <stop offset="100%" stopColor="#fbbf24" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid stroke="rgba(63,63,70,0.3)" strokeDasharray="2 4" />
                <XAxis dataKey="month" stroke="#71717a" fontSize={9} tickFormatter={v => `${v}m`} />
                <YAxis stroke="#71717a" fontSize={9} tickFormatter={v => fmtMoney(v)} width={50} />
                <Tooltip
                  contentStyle={{ background: "#0a0a0a", border: "1px solid #3f3f46", fontSize: 11 }}
                  labelStyle={{ color: "#a1a1aa" }}
                  formatter={(v) => [fmtMoney(v), "Projected"]}
                  labelFormatter={(v) => `Month ${v}`}
                />
                <Area type="monotone" dataKey="value" stroke="#fbbf24" strokeWidth={2} fill="url(#goalArea)" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
          <p className={`text-[11px] mt-1 ${willHit ? "text-emerald-300" : "text-amber-300"}`}>
            At <span className="font-data">${contrib.toFixed(0)}</span>/mo and <span className="font-data">{(apr * 100).toFixed(0)}%</span> returns, you'll have <span className="font-data">{fmtMoney(projectedFinal)}</span> by the target date — {willHit ? "you hit your goal." : `${fmtMoney(dispTarget - projectedFinal)} short.`}
          </p>
        </div>

        <div className="rounded-sm border border-zinc-800 bg-zinc-950/40 p-3 space-y-3">
          <div>
            <div className="flex items-center justify-between text-[10px] mb-1">
              <span className="uppercase tracking-wider text-zinc-500">Monthly contribution</span>
              <span className="font-data text-amber-300">${Number(contrib).toFixed(0)}</span>
            </div>
            <input
              type="range" min="0" max={Math.max(2000, Math.ceil(required * 2))} step="10"
              value={contrib}
              onChange={e => setContrib(Number(e.target.value))}
              data-testid="whatif-contrib"
              className="w-full accent-amber-500"
            />
            <p className="text-[9px] text-zinc-600 mt-0.5">Required: {fmtMoney(required)}/mo</p>
          </div>
          <div>
            <p className="text-[10px] uppercase tracking-wider text-zinc-500 mb-1">Risk / Return</p>
            <div className="grid grid-cols-4 gap-1">
              {Object.entries(RISK_META).map(([k, r]) => (
                <button
                  key={k}
                  onClick={() => setRiskKey(k)}
                  data-testid={`whatif-risk-${k}`}
                  className={`px-1 py-1 text-[9px] rounded-sm border ${
                    riskKey === k ? "border-amber-500/60 bg-amber-500/15 text-amber-200" : "border-zinc-800 text-zinc-400 hover:border-zinc-700"
                  }`}
                >
                  {r.label}
                  <span className="block text-[8px] text-zinc-600">
                    {k === "custom" ? `${(customApr * 100).toFixed(customApr * 100 % 1 === 0 ? 0 : 1)}%` : `${(r.apr * 100).toFixed(0)}%`}
                  </span>
                </button>
              ))}
            </div>
            {riskKey === "custom" && (
              <div className="mt-2 flex items-center gap-2" data-testid="whatif-custom-apr">
                <PercentInput
                  value={customApr}
                  onChange={setCustomApr}
                  dataTestId="whatif-custom-apr-input"
                  className="w-20 bg-zinc-950/60 border border-amber-500/40 rounded-sm px-2 py-1 text-[11px] text-amber-200 font-data focus:outline-none focus:border-amber-500"
                />
                <span className="text-[10px] text-zinc-500">% expected annual return</span>
              </div>
            )}
          </div>
          <label className="flex items-center gap-2 text-[10px] text-zinc-400 cursor-pointer">
            <input type="checkbox" checked={adjustForInflation} onChange={e => setAdjustForInflation(e.target.checked)} data-testid="whatif-inflation" className="accent-amber-500" />
            Inflation-adjust target ({(INFLATION_RATE * 100).toFixed(0)}%/yr)
            {adjustForInflation && <span className="text-amber-300 font-data ml-auto">{fmtMoney(inflTarget)}</span>}
          </label>
        </div>
      </div>

      {/* Nugget tip */}
      <a
        href={nugget.link}
        className="block rounded-sm border border-amber-500/30 bg-gradient-to-r from-amber-500/10 to-transparent p-3 hover:border-amber-500/50 transition-colors"
        data-testid="goal-nugget"
      >
        <div className="flex items-start gap-2">
          <Lightbulb size={14} weight="fill" className="text-amber-400 shrink-0 mt-0.5" />
          <div className="min-w-0">
            <p className="text-[10px] uppercase tracking-wider text-amber-400 font-bold mb-0.5">Golden Nugget</p>
            <p className="text-[12px] font-bold text-zinc-100">{nugget.title}</p>
            <p className="text-[11px] text-zinc-400 mt-1 leading-relaxed">{nugget.text}</p>
            <p className="text-[10px] text-amber-300 mt-1.5">Read more →</p>
          </div>
        </div>
      </a>

      {/* Recent contributions */}
      {goal.contributions?.length > 0 && (
        <div className="rounded-sm border border-zinc-800 bg-zinc-950/40 p-3">
          <p className="text-[10px] uppercase tracking-wider text-zinc-500 mb-2">Recent contributions</p>
          <div className="max-h-40 overflow-y-auto space-y-1">
            {[...goal.contributions].reverse().slice(0, 12).map(c => (
              <div key={c.id} className="flex items-center justify-between text-[11px] py-1 border-b border-zinc-800/40 last:border-0">
                <div className="min-w-0 flex-1">
                  <p className="text-zinc-300 font-data">+{fmtMoney(c.amount)}</p>
                  {c.note && <p className="text-[10px] text-zinc-500 truncate">{c.note}</p>}
                </div>
                <span className="text-[10px] text-zinc-600 shrink-0">{new Date(c.logged_at).toLocaleDateString()}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ------------------------- Log contribution modal -------------------------
function LogModal({ goal, onClose, onSubmit, saving }) {
  const [amount, setAmount] = useState("");
  const [note, setNote] = useState("");
  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={onClose}>
      <div onClick={e => e.stopPropagation()} className="bg-zinc-900 border border-amber-500/40 rounded-sm p-5 max-w-sm w-full" data-testid="log-modal">
        <div className="flex items-center justify-between mb-3">
          <p className="text-xs font-medium text-amber-300">Log contribution — {goal.name}</p>
          <button onClick={onClose} className="text-zinc-500 hover:text-zinc-300"><X size={14} /></button>
        </div>
        <label className="text-[10px] text-zinc-500">Amount</label>
        <div className="relative mt-1 mb-3">
          <span className="absolute left-2 top-1/2 -translate-y-1/2 text-zinc-600 text-xs">$</span>
          <input
            type="number" min="0.01" step="0.01"
            value={amount}
            onChange={e => setAmount(e.target.value)}
            data-testid="log-amount"
            autoFocus
            className="w-full bg-zinc-950/60 border border-zinc-800 rounded-sm pl-5 pr-2 py-2 text-sm text-zinc-100 font-data focus:outline-none focus:border-amber-500/50"
          />
        </div>
        <label className="text-[10px] text-zinc-500">Note (optional)</label>
        <input
          value={note}
          onChange={e => setNote(e.target.value.slice(0, 120))}
          placeholder="e.g. Bonus, side hustle, monthly auto-transfer"
          data-testid="log-note"
          className="w-full mt-1 mb-4 bg-zinc-950/60 border border-zinc-800 rounded-sm px-3 py-2 text-xs text-zinc-300 focus:outline-none focus:border-amber-500/50"
        />
        <div className="flex items-center justify-end gap-2">
          <button onClick={onClose} className="text-[11px] px-3 py-1.5 rounded-sm bg-zinc-800/60 border border-zinc-700 text-zinc-300 hover:bg-zinc-800">Cancel</button>
          <button
            onClick={() => onSubmit(Number(amount), note.trim())}
            disabled={!amount || Number(amount) <= 0 || saving}
            data-testid="log-submit"
            className="text-[11px] px-3 py-1.5 rounded-sm bg-amber-500/25 border border-amber-500/55 text-amber-100 hover:bg-amber-500/35 disabled:opacity-40 inline-flex items-center gap-1"
          >
            {saving ? <Spinner size={11} className="animate-spin" /> : <CheckCircle size={11} />} Log
          </button>
        </div>
      </div>
    </div>
  );
}

// ------------------------- Page -------------------------
export default function GoldenGoals() {
  const { user } = useAuth();
  const [goals, setGoals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isPremium, setIsPremium] = useState(false);
  const [freeLimit, setFreeLimit] = useState(3);
  const [editing, setEditing] = useState(null); // null | {} (new) | goal (edit)
  const [saving, setSaving] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [logTarget, setLogTarget] = useState(null);
  const [nestEgg, setNestEgg] = useState(null);

  const fetchGoals = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/goals`, { withCredentials: true });
      setGoals(r.data.goals || []);
      setIsPremium(!!r.data.is_premium);
      setFreeLimit(r.data.free_limit || 3);
    } catch (err) {
      console.error("Failed to load goals:", err);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchNestEgg = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/goals/nest-egg`, { withCredentials: true });
      setNestEgg(r.data);
    } catch {
      setNestEgg({ available: false });
    }
  }, []);

  useEffect(() => {
    fetchGoals();
    fetchNestEgg();
  }, [fetchGoals, fetchNestEgg]);

  const saveGoal = async (payload) => {
    setSaving(true);
    try {
      if (editing?.id) {
        await axios.patch(`${API}/goals/${editing.id}`, payload, { withCredentials: true });
      } else {
        await axios.post(`${API}/goals`, payload, { withCredentials: true });
      }
      setEditing(null);
      await fetchGoals();
    } catch (err) {
      alert(err.response?.data?.detail || "Failed to save goal");
    } finally {
      setSaving(false);
    }
  };

  const deleteGoal = async (g) => {
    if (!window.confirm(`Delete "${g.name}"? This can't be undone.`)) return;
    try {
      await axios.delete(`${API}/goals/${g.id}`, { withCredentials: true });
      setGoals(prev => prev.filter(x => x.id !== g.id));
      if (selectedId === g.id) setSelectedId(null);
    } catch (err) {
      alert(err.response?.data?.detail || "Failed to delete");
    }
  };

  const logContribution = async (amount, note) => {
    if (!logTarget) return;
    setSaving(true);
    try {
      await axios.post(`${API}/goals/${logTarget.id}/contribute`, { amount, note }, { withCredentials: true });
      setLogTarget(null);
      await fetchGoals();
    } catch (err) {
      alert(err.response?.data?.detail || "Failed to log");
    } finally {
      setSaving(false);
    }
  };

  const selected = goals.find(g => g.id === selectedId);
  const totalSaved = goals.reduce((acc, g) => acc + (g.current_amount || 0), 0);
  const totalTarget = goals.reduce((acc, g) => acc + (g.target_amount || 0), 0);
  const overallPct = totalTarget > 0 ? (totalSaved / totalTarget) * 100 : 0;
  const hitLimit = !isPremium && goals.length >= freeLimit;
  const completedCount = goals.filter(g => g.current_amount >= g.target_amount).length;

  return (
    <div className="p-5 sm:p-6 space-y-5 max-w-[1300px]" data-testid="golden-goals-page">
      {/* Hero */}
      <div className="relative overflow-hidden rounded-sm border border-amber-500/30 bg-zinc-950 p-5 sm:p-6">
        <div className="absolute inset-0 shimmer pointer-events-none" aria-hidden="true" />
        <div className="absolute -top-10 -right-10 w-48 h-48 rounded-full bg-amber-500/15 blur-3xl pointer-events-none" aria-hidden="true" />
        <div className="absolute -bottom-12 -left-12 w-48 h-48 rounded-full bg-violet-500/10 blur-3xl pointer-events-none" aria-hidden="true" />
        <div className="relative flex items-start justify-between flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <div className="relative">
              <Egg size={42} weight="fill" className="text-amber-400 drop-shadow-[0_0_12px_rgba(251,191,36,0.7)]" />
            </div>
            <div className="min-w-0">
              <p className="text-[10px] text-amber-400 uppercase tracking-wider font-bold flex items-center gap-1.5">
                <span className="live-dot" /> Goal Tracker
              </p>
              <h1 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight text-gradient-gold">Golden Goals</h1>
              <p className="text-xs text-zinc-400 mt-1">Set the target. Track the climb. Watch the nest fill.</p>
            </div>
          </div>
          <button
            onClick={() => setEditing({})}
            disabled={hitLimit}
            data-testid="new-goal-btn"
            className="inline-flex items-center gap-1.5 text-xs px-4 py-2.5 rounded-sm bg-gradient-to-r from-amber-500/30 to-amber-600/20 border border-amber-500/60 text-amber-100 hover:from-amber-500/40 hover:to-amber-600/30 disabled:opacity-50 disabled:cursor-not-allowed glow-hover font-medium"
            title={hitLimit ? `Free tier limited to ${freeLimit} goals — upgrade to Gold for unlimited` : ""}
          >
            <Plus size={12} weight="bold" /> New Goal
          </button>
        </div>
      </div>

      {/* Stats strip */}
      {!loading && goals.length > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <div className="rounded-sm border border-zinc-800 bg-zinc-900/40 p-3">
            <p className="text-[9px] uppercase text-zinc-500">Active goals</p>
            <p className="font-heading text-xl font-bold text-zinc-100 mt-1">{goals.length} {!isPremium && <span className="text-[11px] text-zinc-500 font-data">/ {freeLimit}</span>}</p>
          </div>
          <div className="rounded-sm border border-zinc-800 bg-zinc-900/40 p-3">
            <p className="text-[9px] uppercase text-zinc-500">Total saved</p>
            <p className="font-heading text-xl font-bold text-emerald-300 mt-1">{fmtMoney(totalSaved)}</p>
          </div>
          <div className="rounded-sm border border-zinc-800 bg-zinc-900/40 p-3">
            <p className="text-[9px] uppercase text-zinc-500">Total target</p>
            <p className="font-heading text-xl font-bold text-amber-300 mt-1">{fmtMoney(totalTarget)}</p>
          </div>
          <div className="rounded-sm border border-zinc-800 bg-zinc-900/40 p-3">
            <p className="text-[9px] uppercase text-zinc-500">Overall progress</p>
            <p className="font-heading text-xl font-bold text-zinc-100 mt-1">{overallPct.toFixed(1)}%</p>
            {completedCount > 0 && <p className="text-[9px] text-amber-400 mt-0.5"><Trophy size={9} weight="fill" className="inline mr-0.5" /> {completedCount} hatched</p>}
          </div>
        </div>
      )}

      {/* Tier banner */}
      {hitLimit && (
        <div className="rounded-sm border border-amber-500/40 bg-amber-500/10 p-3 flex items-center gap-3 flex-wrap" data-testid="tier-limit-banner">
          <Lightning size={16} weight="fill" className="text-amber-400 shrink-0" />
          <div className="text-[12px] flex-1 min-w-0">
            <p className="text-amber-300 font-medium">You've hit the free tier limit ({freeLimit} goals).</p>
            <p className="text-[11px] text-zinc-400">Upgrade to <strong className="text-amber-300">Gold tier</strong> for unlimited goals + Silly Goose access.</p>
          </div>
        </div>
      )}

      {/* Wizard / Editor */}
      {editing && (
        <GoalWizard
          initial={editing}
          nestEgg={nestEgg}
          onSave={saveGoal}
          onCancel={() => setEditing(null)}
          saving={saving}
        />
      )}

      {/* Detail panel */}
      {selected && (
        <GoalDetail
          goal={selected}
          onLog={(g) => setLogTarget(g)}
          onEdit={(g) => { setEditing(g); setSelectedId(null); }}
          onDelete={deleteGoal}
          onClose={() => setSelectedId(null)}
        />
      )}

      {/* Goals grid */}
      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <Skeleton className="h-40 bg-zinc-900/40" />
          <Skeleton className="h-40 bg-zinc-900/40" />
        </div>
      ) : goals.length === 0 ? (
        <div className="rounded-sm border border-dashed border-zinc-800 bg-zinc-900/20 p-12 text-center" data-testid="empty-state">
          <Egg size={48} weight="fill" className="text-amber-500/40 mx-auto mb-3" />
          <h3 className="font-heading text-lg font-bold text-zinc-100 mb-1">No goals yet</h3>
          <p className="text-[12px] text-zinc-500 max-w-sm mx-auto mb-4">
            Set a target — emergency fund, house deposit, retirement nest. We'll do the math, you do the climb.
          </p>
          <button
            onClick={() => setEditing({})}
            data-testid="empty-new-goal-btn"
            className="inline-flex items-center gap-1.5 text-xs px-3.5 py-2 rounded-sm bg-amber-500/20 border border-amber-500/50 text-amber-200 hover:bg-amber-500/30"
          >
            <Plus size={12} weight="bold" /> Create your first goal
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3" data-testid="goals-grid">
          {goals.map(g => (
            <button
              key={g.id}
              onClick={() => setSelectedId(g.id === selectedId ? null : g.id)}
              className="text-left"
              data-testid={`goal-tile-${g.id}`}
            >
              <GoalCard
                goal={g}
                onLog={(g) => setLogTarget(g)}
                onEdit={(g) => { setEditing(g); setSelectedId(null); }}
                onDelete={deleteGoal}
              />
            </button>
          ))}
        </div>
      )}

      {/* Log modal */}
      {logTarget && (
        <LogModal
          goal={logTarget}
          onClose={() => setLogTarget(null)}
          onSubmit={logContribution}
          saving={saving}
        />
      )}

      {!loading && goals.length > 0 && (
        <p className="text-[10px] text-zinc-600 text-center pt-2">
          Tip: tap any goal card to open the what-if simulator and projection chart.
        </p>
      )}
    </div>
  );
}
